import React, { useEffect, useMemo, useState } from 'react';
import {
	StyledButton,
	StyledContainer,
	StyledCopyButton,
	StyledReadonlyTextField,
	StyledSubWrapper,
} from './EdgeConfigsCode.styles';
import {
	useGlobalization,
	success as notificationSuccess,
	useIsFeatureEnabled,
	selectSettings,
} from '@innovyze/stylovyze';
import { useDispatch, useSelector } from 'react-redux';
import { genConfCode, genConfCodeDynamic } from '@Actions';
import { getValue } from '@Selectors';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SpinnerStatusIndicator } from '@Pages/EditSensor/SpinnerStatusIndicator';
import { TimeRange } from '@Utils/timeRange';
import { DynamicConfigsPrompt } from './DynamicConfigsPrompt';
import { useDynamicConfigsDialog } from '@Hooks';
import { FLAGS } from '@Types';

type GenCodeReq = {
	poll: boolean;
	firstTime?: boolean;
};

type EdgeConfigsCodeProps = {
	dynamicCerts: boolean;
};

const EdgeConfigsCode = (props: EdgeConfigsCodeProps) => {
	const { dynamicCerts } = props;
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const { companySettings } = selectSettings();
	const { configCode } = useSelector(getValue).implementation;
	const { open: dynCnfDlgOpen, handleSetOpen: dynCnfDlgSetOpen } =
		useDynamicConfigsDialog();
	const [timeLeftMin, setTimeLeftMin] = useState(0);

	const isDynamicCertsEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_DYNAMIC_CERTS,
	) as unknown as boolean;
	const isGreengrassPorts443Enabled = useIsFeatureEnabled(
		FLAGS.ENABLE_GREENGRASS_PORTS_443,
	) as unknown as boolean;

	const genCodeText = useMemo(() => {
		if (!configCode?.response?.code) return t('Generate Code');
		return t('Regenerate Code');
	}, [configCode?.response?.code, configCode?.response]);

	const genCode = (req: GenCodeReq) => {
		const { poll, firstTime = false } = req;
		const deviceId = configCode?.request?.deviceId ?? '';
		const useDefaultPorts = isGreengrassPorts443Enabled;
		const codeAction = dynamicCerts
			? genConfCodeDynamic({ poll, deviceId, firstTime, useDefaultPorts })
			: genConfCode({ poll, deviceId, firstTime, useDefaultPorts });
		dispatch(codeAction);
	};

	useEffect(() => {
		// * runs at the beggining once
		if (!dynamicCerts && !configCode?.response?.code) {
			genCode({ firstTime: true, poll: true }); // ? if no code is saved in store/state (redux)
		}
	}, []);

	// * Hook to reduce the code expiration time every minute without leaving the screen
	useEffect(() => {
		const expiresAt = configCode?.response?.expiresAt ?? 0;
		setTimeLeftMin(TimeRange.minutesDifference(expiresAt));
		const interval = setInterval(
			() => setTimeLeftMin(TimeRange.minutesDifference(expiresAt)),
			1000 * 60, // ? 60 sec
		);
		return () => {
			clearInterval(interval);
		};
	}, [configCode?.response?.expiresAt, configCode?.response]);

	const minutesLeftText = useMemo(() => {
		const expiresAt = configCode?.response?.expiresAt;
		if (!expiresAt) return '';
		const minutesLeft = TimeRange.minutesDifference(expiresAt);
		if (minutesLeft <= 0) return t('code expired');
		return `${minutesLeft} ${t('minutes')}`;
	}, [timeLeftMin]);

	const handleGenCodeButtonClick = () => {
		if (isDynamicCertsEnabled) {
			dynCnfDlgSetOpen(true); // ? opens dynamic configs warning dialog
		} else {
			genCode({ poll: false });
		}
	};

	const handleCopyButtonClick = () => {
		const code = configCode?.response?.code;
		if (code) {
			navigator.clipboard.writeText(code);
			dispatch(notificationSuccess(t('Code was copied')));
		}
	};

	const isGeneratingCode = configCode?.status === 'pending';
	const statusText =
		isGeneratingCode && !configCode?.firstTime
			? t('Generating')
			: t('Loading');

	const onConfirmDynCnfDlgHandler = () => {
		genCode({ firstTime: true, poll: true });
	};

	return (
		<StyledContainer>
			{isDynamicCertsEnabled && (
				<DynamicConfigsPrompt
					open={dynCnfDlgOpen}
					setOpenHandler={dynCnfDlgSetOpen}
					onConfirm={onConfirmDynCnfDlgHandler}
					tenantName={companySettings?.organizationName || ''}
				/>
			)}

			<StyledReadonlyTextField
				name={'configurationCode'}
				label={t('Installation Code')}
				placeholder={t('Installation Code')}
				value={timeLeftMin > 0 ? configCode?.response?.code ?? '' : ''}
				margin="dense"
				fullWidth
				helperText={minutesLeftText}
				maxRows={1}
				disabled={isGeneratingCode}
				InputProps={{
					readOnly: true,
					endAdornment: (
						<StyledSubWrapper>
							<SpinnerStatusIndicator
								text={statusText}
								visible={isGeneratingCode}
							/>

							<StyledCopyButton
								data-cy="copyCodeButton"
								color={'primary'}
								variant="text"
								onClick={handleCopyButtonClick}
								disabled={!configCode?.response?.code}>
								<ContentCopyIcon />
								{t('Copy')}
							</StyledCopyButton>

							<StyledButton
								data-cy="generateConfigurationCodeButton"
								color={'primary'}
								variant="contained"
								onClick={handleGenCodeButtonClick}
								disabled={isGeneratingCode}>
								{genCodeText}
							</StyledButton>
						</StyledSubWrapper>
					),
				}}
			/>
		</StyledContainer>
	);
};

export default EdgeConfigsCode;
